import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from '../ui/command';
import {
  LayoutDashboard,
  Users,
  CreditCard,
  BellRing,
  FileBarChart,
  ShieldCheck,
  ClipboardList,
  Mail,
  CalendarDays,
  Link,
  HelpCircle,
  Settings,
  Wrench,
  FileText,
  Search,
} from 'lucide-react';

const pages = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard, group: 'Navigation' },
  { name: 'Clients', href: '/clients', icon: Users, group: 'Navigation' },
  { name: 'Tenant Matcher', href: '/tenant-matcher', icon: Link, group: 'Navigation' },
  { name: 'Subscriptions', href: '/subscriptions', icon: CreditCard, group: 'Navigation' },
  { name: 'Renewal Calendar', href: '/calendar', icon: CalendarDays, group: 'Navigation' },
  { name: 'Alerts', href: '/alerts', icon: BellRing, group: 'Monitoring' },
  { name: 'Reports', href: '/reports', icon: FileBarChart, group: 'Monitoring' },
  { name: 'Security Reports', href: '/scheduled-reports', icon: ShieldCheck, group: 'Monitoring' },
  { name: 'Audit Log', href: '/audit-logs', icon: ClipboardList, group: 'Monitoring' },
  { name: 'Message Trace', href: '/message-trace', icon: Mail, group: 'Monitoring' },
  { name: 'Help', href: '/help', icon: HelpCircle, group: 'Other' },
  { name: 'Changelog', href: '/changelog', icon: FileText, group: 'Other' },
  { name: 'My Settings', href: '/settings/user', icon: Settings, group: 'Other' },
  { name: 'System Settings', href: '/settings/system', icon: Wrench, group: 'Other' },
];

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      setOpen((prev) => !prev);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const runCommand = useCallback((command: () => void) => {
    setOpen(false);
    command();
  }, []);

  const grouped = pages.reduce((acc, page) => {
    if (!acc[page.group]) acc[page.group] = [];
    acc[page.group].push(page);
    return acc;
  }, {} as Record<string, typeof pages>);

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search pages, clients, actions..." />
      <CommandList>
        <CommandEmpty>
          <div className="flex flex-col items-center gap-2 py-4">
            <Search className="h-8 w-8 text-muted-foreground/50" />
            <p>No results found.</p>
          </div>
        </CommandEmpty>
        {Object.entries(grouped).map(([group, items], i) => (
          <div key={group}>
            {i > 0 && <CommandSeparator />}
            <CommandGroup heading={group}>
              {items.map((item) => (
                <CommandItem
                  key={item.href}
                  value={item.name}
                  onSelect={() => runCommand(() => navigate(item.href))}
                >
                  <item.icon className="mr-2 h-4 w-4" />
                  <span>{item.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </div>
        ))}
      </CommandList>
    </CommandDialog>
  );
}
